/**
 * OCC Benchmark 报表（halo ``occ_benchmark`` 口径，仅 ``occ / occ_benchmark_check`` 且有
 * ``extras.halo`` 时渲染；老数据仍走通用视图）。
 *
 * 结构对齐 halo 报表：概览 / 评测集分布 / Metrics 说明 / 语义指标 / 占据指标 / GOD 指标 六个页签，
 * 顶部分段开关切换两种评测口径：
 * - 前融合（默认）：语义主指标 mIoU，GOD 用 Instance-IoU（连通块匈牙利匹配，IoU≥0.2）；
 * - 纯视觉：语义主指标 Macro RayIoU，GOD 用 LET-Ray（射线首阻挡面，前向扇区）。
 * 占据指标两种口径共用。
 *
 * 所有数值由后端把各 case 计数求和后按 halo render SQL 重算（EPS=1e-6，分母 0 → 0），
 * 前端只做展示；比率 0~1（展示 ×100），Δ 已是 pp。
 */
import { useMemo, useState } from 'react';
import { Button, Segmented, Table, Tabs, Tag, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import type {
  OccBenchmarkCase,
  OccBenchmarkReport as OccBenchmarkReportData,
  OccGodRow,
  OccHaloHeadline,
  OccIouRow,
  OccLetRayRow,
  OccRayIouClass,
  OccSpatialCell,
} from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { sceneGroupDisplay } from '@/constants/sceneGroup';

import { deltaColor, EMPTY, fmtCount, fmtPp, fmtRatio } from '../format';
import OccHeatmap, { type HeatCell } from './OccHeatmap';
import {
  buildMetricHelp,
  COVERAGE_TEXT,
  HeatmapReadingNote,
  HelpLabel,
  type OccHelpKey,
  ReadingGuide,
  withTech,
} from './OccHelp';
import SideInfo from './SideInfo';

export type OccVariant = 'fusion' | 'vision';

const VARIANT_OPTIONS = [
  { value: 'fusion', label: '前融合（mIoU + Instance-IoU GOD）' },
  { value: 'vision', label: '纯视觉（Macro RayIoU + LET-Ray GOD）' },
];

const UNCATEGORIZED = '__uncategorized__';

/** 「Metrics 说明」表的一行：白话解释在前，严格公式在后。 */
interface MetricGuideRow {
  group: string;
  name: string;
  plain: React.ReactNode;
  formula: string;
  note: string;
}

const EVAL_REGION_CN: Record<string, string> = {
  sim_footprint: '仿真覆盖范围',
  full_frame: '整帧',
};

const fmtNum = (v: number | null | undefined, digits: number): string =>
  v === null || v === undefined ? EMPTY : v.toFixed(digits);

/** 数值列排序：null 排最后。 */
const byNum =
  <T,>(get: (r: T) => number | null | undefined) =>
  (a: T, b: T): number => {
    const x = get(a);
    const y = get(b);
    if (x === null || x === undefined) return y === null || y === undefined ? 0 : 1;
    if (y === null || y === undefined) return -1;
    return x - y;
  };

// ───────────────────────────── 小部件 ─────────────────────────────────────────

interface PanelProps {
  title: React.ReactNode;
  code?: string;
  desc?: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
}

/** halo 报表的 panel：标题 + 源表名（code）+ 内容 + 口径说明。 */
function Panel({ title, code, desc, extra, children }: PanelProps) {
  return (
    <div className="pe-report-card">
      <div className="pe-report-card-title">
        {title}
        {code && <code className="occ-panel-code">{code}</code>}
        {extra && <span style={{ marginLeft: 'auto', fontWeight: 400 }}>{extra}</span>}
      </div>
      {children}
      {desc && <div className="occ-panel-desc">{desc}</div>}
    </div>
  );
}

interface KpiProps {
  label: string;
  value: number | null | undefined;
  delta?: number | null;
  sub?: string;
  /** 白话解释（问号 tooltip） */
  help?: React.ReactNode;
  primary?: boolean;
}

function Kpi({ label, value, delta, sub, help, primary }: KpiProps) {
  return (
    <div className={`pe-kpi${primary ? ' occ-kpi--primary' : ''}`}>
      <div className="pe-kpi-label">
        <HelpLabel label={label} help={help} />
      </div>
      <div className="pe-kpi-value">
        {fmtRatio(value)}
        {delta !== undefined && delta !== null && (
          <span className="pe-kpi-delta" style={{ color: deltaColor(delta) }}>
            {fmtPp(delta)}
          </span>
        )}
      </div>
      <div className="pe-kpi-sub">{sub || ' '}</div>
    </div>
  );
}

const ratioCol = <T,>(
  title: React.ReactNode,
  get: (r: T) => number | null | undefined,
  key: string,
  width = 96,
): ColumnsType<T>[number] => ({
  title,
  key,
  width,
  align: 'right',
  sorter: byNum(get),
  render: (_: unknown, r: T) => fmtRatio(get(r)),
});

const countCol = <T,>(
  title: React.ReactNode,
  get: (r: T) => number | null | undefined,
  key: string,
  width = 100,
): ColumnsType<T>[number] => ({
  title,
  key,
  width,
  align: 'right',
  sorter: byNum(get),
  render: (_: unknown, r: T) => fmtCount(get(r)),
});

const classCell = (name: string | null, inMean?: boolean | null, extra?: React.ReactNode) => (
  <span>
    <code className="pe-ck-code">{name || EMPTY}</code>
    {inMean === false && (
      <Tooltip title="不计入均值（真值 / 并集为 0）">
        <Tag bordered={false} style={{ marginLeft: 6, fontSize: 11 }}>
          不计入
        </Tag>
      </Tooltip>
    )}
    {extra}
  </span>
);

const modeCn = (mode: string | null): string =>
  mode === 'class_agnostic' ? 'class_agnostic（不分类）' : mode === 'class_aware' ? 'class_aware（分类）' : mode || EMPTY;

/** status 四种状态的白话名（status 由语义类别合成，见 Metrics 说明）。 */
const STATUS_CN: Record<string, string> = {
  unknown: '空白',
  free: '能走（路面）',
  occupied: '被固定物体挡住',
  filter: '被车·人等会动的东西挡住',
};

const sectorCn =(s: string | null): string =>
  s === 'front' ? 'front（前向）' : s === 'side_rear' ? 'side_rear（侧后）' : s === 'all' ? 'all（全部）' : s || EMPTY;

/** 合计 / 均值行（mIoU、mF1 等）的样式。 */
const summaryRowClass = (r: { className?: string | null }) =>
  r.className === '__mean__' ? 'pe-row-all' : '';

// ───────────────────────────── 表格 ───────────────────────────────────────────

function SemanticIouTable({ rows, miou }: { rows: OccIouRow[]; miou: number | null }) {
  const data = [
    ...rows,
    { className: '__mean__', iou: miou } as unknown as OccIouRow,
  ];
  const columns: ColumnsType<OccIouRow> = [
    {
      title: 'class_name',
      key: 'className',
      width: 200,
      render: (_, r) => (r.className === '__mean__' ? <b>mIoU</b> : classCell(r.className, r.inMean)),
    },
    {
      title: 'IoU',
      key: 'iou',
      width: 90,
      align: 'right',
      render: (_, r) => fmtRatio(r.iou),
    },
    {
      title: '精确率',
      key: 'precision',
      width: 90,
      align: 'right',
      render: (_, r) => (r.className === '__mean__' ? '' : fmtRatio(r.precision)),
    },
    {
      title: '召回率',
      key: 'recall',
      width: 90,
      align: 'right',
      render: (_, r) => (r.className === '__mean__' ? '' : fmtRatio(r.recall)),
    },
    ...(['i', 'u', 'g', 'p'] as const).map((k) => ({
      title: `Σ${k === 'g' ? 'GT' : k === 'p' ? 'Pred' : k.toUpperCase()}`,
      key: k,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, r: OccIouRow) => (r.className === '__mean__' ? '' : fmtCount(r[k])),
    })),
  ];
  return (
    <Table<OccIouRow>
      rowKey={(r) => r.className || r.status || ''}
      size="small"
      dataSource={data}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 900 }}
      rowClassName={summaryRowClass}
    />
  );
}

function StatusTable({ rows, miou }: { rows: OccIouRow[]; miou: number | null }) {
  const data = [...rows, { status: '__mean__', iou: miou } as unknown as OccIouRow];
  const columns: ColumnsType<OccIouRow> = [
    {
      title: 'status',
      key: 'status',
      width: 260,
      render: (_, r) =>
        r.status === '__mean__' ? (
          <b>status mIoU</b>
        ) : (
          <span>
            <code className="pe-ck-code">{r.status}</code>
            {r.status && STATUS_CN[r.status] && <span className="occ-status-cn">{STATUS_CN[r.status]}</span>}
          </span>
        ),
    },
    { title: 'IoU', key: 'iou', width: 90, align: 'right', render: (_, r) => fmtRatio(r.iou) },
    ...(['i', 'u', 'g', 'p'] as const).map((k) => ({
      title: `Σ${k === 'g' ? 'GT' : k === 'p' ? 'Pred' : k.toUpperCase()}`,
      key: k,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, r: OccIouRow) => (r.status === '__mean__' ? '' : fmtCount(r[k])),
    })),
  ];
  return (
    <Table<OccIouRow>
      rowKey={(r) => r.status || ''}
      size="small"
      dataSource={data}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 700 }}
      rowClassName={(r) => (r.status === '__mean__' ? 'pe-row-all' : '')}
    />
  );
}

function RayIouClassTable({ rows, macro }: { rows: OccRayIouClass[]; macro: number | null }) {
  const data = [...rows, { className: '__mean__', rayIou: macro } as unknown as OccRayIouClass];
  const columns: ColumnsType<OccRayIouClass> = [
    {
      title: 'class_name',
      key: 'className',
      width: 200,
      render: (_, r) => (r.className === '__mean__' ? <b>Macro RayIoU</b> : classCell(r.className, r.inMean)),
    },
    { title: 'RayIoU', key: 'rayIou', width: 100, align: 'right', render: (_, r) => fmtRatio(r.rayIou) },
    ...(['gt', 'pred', 'matched'] as const).map((k) => ({
      title: k,
      key: k,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, r: OccRayIouClass) => (r.className === '__mean__' ? '' : fmtCount(r[k])),
    })),
  ];
  return (
    <Table<OccRayIouClass>
      rowKey="className"
      size="small"
      dataSource={data}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 640 }}
      rowClassName={summaryRowClass}
    />
  );
}

type LetRayKind = 'overview' | 'class' | 'sector';

function LetRayTable({
  rows,
  kind,
  mf1l,
}: {
  rows: OccLetRayRow[];
  kind: LetRayKind;
  mf1l?: number | null;
}) {
  const data =
    kind === 'class'
      ? [...rows, { className: '__mean__', f1L: mf1l } as unknown as OccLetRayRow]
      : rows;
  const isMean = (r: OccLetRayRow) => r.className === '__mean__';
  const first: ColumnsType<OccLetRayRow>[number] =
    kind === 'overview'
      ? { title: 'mode', key: 'mode', width: 170, fixed: 'left', render: (_, r) => modeCn(r.mode) }
      : kind === 'sector'
        ? { title: 'sector', key: 'sector', width: 150, fixed: 'left', render: (_, r) => sectorCn(r.sector) }
        : {
            title: 'class_name',
            key: 'className',
            width: 190,
            fixed: 'left',
            render: (_, r) => (isMean(r) ? <b>LET-Ray mF1L</b> : classCell(r.className, r.inMean)),
          };
  const r = (fn: (x: OccLetRayRow) => React.ReactNode) => (_: unknown, x: OccLetRayRow) =>
    isMean(x) ? '' : fn(x);
  const columns: ColumnsType<OccLetRayRow> = [
    first,
    ...(kind !== 'sector'
      ? ([
          { title: 'valid_ray', key: 'v', width: 96, align: 'right', render: r((x) => fmtCount(x.validRayCount)) },
        ] as ColumnsType<OccLetRayRow>)
      : []),
    { title: 'gt', key: 'gt', width: 90, align: 'right', render: r((x) => fmtCount(x.gt)) },
    { title: 'pred', key: 'pred', width: 90, align: 'right', render: r((x) => fmtCount(x.pred)) },
    { title: 'matched', key: 'm', width: 90, align: 'right', render: r((x) => fmtCount(x.matched)) },
    { title: 'recall', key: 'recall', width: 86, align: 'right', render: r((x) => fmtRatio(x.recall)) },
    { title: 'precision', key: 'precision', width: 90, align: 'right', render: r((x) => fmtRatio(x.precision)) },
    {
      title: <Tooltip title="Σaffinity / ΣPred">precision_L</Tooltip>,
      key: 'pl',
      width: 100,
      align: 'right',
      render: r((x) => fmtRatio(x.precisionL)),
    },
    { title: 'RayIoU', key: 'ri', width: 86, align: 'right', render: r((x) => fmtRatio(x.rayIou)) },
    {
      title: <Tooltip title="2·R·PL / (R+PL)">F1L</Tooltip>,
      key: 'f1l',
      width: 86,
      align: 'right',
      render: (_, x) => (isMean(x) ? <b>{fmtRatio(x.f1L)}</b> : fmtRatio(x.f1L)),
    },
    {
      title: <Tooltip title="Σaffinity / ΣMatched">mean_LA</Tooltip>,
      key: 'la',
      width: 90,
      align: 'right',
      render: r((x) => (x.matched ? fmtRatio(x.meanLa) : EMPTY)),
    },
    {
      title: <Tooltip title="ΣFalseBlock / max(Σvalid − ΣGT, 0)：GT 为空但预测阻挡">false_block</Tooltip>,
      key: 'fbr',
      width: 100,
      align: 'right',
      render: r((x) => fmtRatio(x.falseBlockRate)),
    },
    {
      title: <Tooltip title="ΣMissedBlock / ΣGT：GT 阻挡但预测为空">missed_block</Tooltip>,
      key: 'mbr',
      width: 104,
      align: 'right',
      render: r((x) => fmtRatio(x.missedBlockRate)),
    },
    {
      title: <Tooltip title="Σ|距离误差| / ΣMatched（m）">mean_abs_err</Tooltip>,
      key: 'mae',
      width: 104,
      align: 'right',
      render: r((x) => (x.matched ? `${fmtNum(x.meanAbsErrorM, 3)} m` : EMPTY)),
    },
    { title: 'err_p50', key: 'p50', width: 90, render: r((x) => x.errorP50Bucket || EMPTY) },
    { title: 'err_p90', key: 'p90', width: 90, render: r((x) => x.errorP90Bucket || EMPTY) },
  ];
  return (
    <Table<OccLetRayRow>
      rowKey={(x) => `${x.mode}-${x.sector}-${x.className}`}
      size="small"
      dataSource={data}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1600 }}
      rowClassName={summaryRowClass}
    />
  );
}

function ErrorHistogram({ rows, buckets }: { rows: OccLetRayRow[]; buckets: string[] }) {
  if (!rows.length || !buckets.length) return <div className="pe-report-empty">无距离误差数据</div>;
  return (
    <div className="occ-hist">
      {rows.map((row) => {
        const total = row.errorHist.reduce((a, b) => a + b, 0);
        return (
          <div key={row.mode || ''} className="occ-hist-group">
            <div className="occ-hist-title">
              {modeCn(row.mode)} · 命中射线 {fmtCount(total)} · 平均绝对误差 {fmtNum(row.meanAbsErrorM, 3)} m
            </div>
            {buckets.map((b, i) => {
              const n = row.errorHist[i] ?? 0;
              const share = total ? n / total : 0;
              return (
                <div key={b} className="occ-hist-row">
                  <span className="occ-hist-label">{b}</span>
                  <span className="occ-hist-bar">
                    <span style={{ width: `${(share * 100).toFixed(1)}%` }} />
                  </span>
                  <span className="occ-hist-val">
                    {fmtCount(n)}（{fmtRatio(share, 1)}）
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function GodTable({
  rows,
  kind,
  mf1,
  primaryIou,
}: {
  rows: OccGodRow[];
  kind: 'overview' | 'threshold' | 'class';
  mf1?: number | null;
  primaryIou?: string | null;
}) {
  const data =
    kind === 'class' ? [...rows, { className: '__mean__', f1: mf1 } as unknown as OccGodRow] : rows;
  const isMean = (r: OccGodRow) => r.className === '__mean__';
  const r = (fn: (x: OccGodRow) => React.ReactNode) => (_: unknown, x: OccGodRow) =>
    isMean(x) ? '' : fn(x);
  const lead: ColumnsType<OccGodRow> =
    kind === 'overview'
      ? [{ title: 'mode', key: 'mode', width: 180, render: (_, x) => modeCn(x.mode) }]
      : kind === 'threshold'
        ? [
            { title: 'iou_threshold', key: 'th', width: 120, render: (_, x) => x.iouThreshold },
            { title: 'mode', key: 'mode', width: 180, render: (_, x) => modeCn(x.mode) },
          ]
        : [
            {
              title: 'class_name',
              key: 'cls',
              width: 220,
              render: (_, x) =>
                isMean(x) ? (
                  <b>god_mF1{primaryIou ? `@${primaryIou.replace('IoU@', '')}` : ''}</b>
                ) : (
                  classCell(
                    x.className,
                    x.gt > 0 ? null : false,
                    x.matchRule === 'curb_distance' ? (
                      <Tooltip title="curb 按距离容差单独统计：recall 取 god_curb_recall 行、precision 取 god_curb_precision 行">
                        <Tag color="orange" bordered={false} style={{ marginLeft: 6, fontSize: 11 }}>
                          距离容差
                        </Tag>
                      </Tooltip>
                    ) : null,
                  )
                ),
            },
          ];
  const columns: ColumnsType<OccGodRow> = [
    ...lead,
    { title: 'matched', key: 'tp', width: 90, align: 'right', render: r((x) => fmtCount(x.tp)) },
    { title: 'gt', key: 'gt', width: 90, align: 'right', render: r((x) => fmtCount(x.gt)) },
    { title: 'pred', key: 'pred', width: 90, align: 'right', render: r((x) => fmtCount(x.pred)) },
    { title: 'recall', key: 'recall', width: 90, align: 'right', render: r((x) => fmtRatio(x.recall)) },
    { title: 'precision', key: 'precision', width: 90, align: 'right', render: r((x) => fmtRatio(x.precision)) },
    {
      title: 'F1',
      key: 'f1',
      width: 90,
      align: 'right',
      render: (_, x) => (isMean(x) ? <b>{fmtRatio(x.f1)}</b> : fmtRatio(x.f1)),
    },
  ];
  return (
    <Table<OccGodRow>
      rowKey={(x) => `${x.iouThreshold}-${x.mode}-${x.className}`}
      size="small"
      dataSource={data}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 760 }}
      rowClassName={summaryRowClass}
    />
  );
}

// ───────────────────────────── 主组件 ─────────────────────────────────────────

interface OccBenchmarkReportProps {
  report: OccBenchmarkReportData;
  onViewLog: (taskId: number) => void;
}

const HEADLINE_LABELS: { key: keyof OccHaloHeadline; label: string; variant?: OccVariant }[] = [
  { key: 'miou', label: '语义 mIoU', variant: 'fusion' },
  { key: 'godF1', label: 'Instance GOD F1@0.20', variant: 'fusion' },
  { key: 'godMf1', label: 'god_mF1@0.20', variant: 'fusion' },
  { key: 'macroRayiou', label: 'Macro RayIoU', variant: 'vision' },
  { key: 'letrayF1lFront', label: 'LET-Ray F1L（前向）', variant: 'vision' },
  { key: 'letrayMf1l', label: 'LET-Ray mF1L', variant: 'vision' },
  { key: 'occupancyIou', label: '占据 IoU' },
  { key: 'statusMiou', label: 'status mIoU' },
];

export default function OccBenchmarkReport({ report, onViewLog }: OccBenchmarkReportProps) {
  const [variant, setVariant] = useState<OccVariant>('fusion');
  const [tab, setTab] = useState('overview');
  const [godHeatMode, setGodHeatMode] = useState<'aware' | 'agnostic'>('aware');
  const vis = variant === 'vision';
  const { header, summary, headline, occupancy, status, fusion, vision, spatial, compare } = report;
  const delta = compare?.delta ?? null;
  const god = fusion.god;
  const primaryIou = god.primaryIou || 'IoU@0.20';

  const heat = (pick: (c: OccSpatialCell) => number | null): HeatCell[] =>
    spatial.cells.map((c) => ({ x: c.x, y: c.y, value: pick(c), note: `有效格子 ${fmtCount(c.valid)}` }));

  const totalFrames = summary.frames || 0;
  const help = useMemo(() => buildMetricHelp(summary.timeAlignToleranceSec), [summary.timeAlignToleranceSec]);
  const helpLabel = (key: OccHelpKey, label: React.ReactNode) => <HelpLabel label={label} help={help[key]} />;

  // 评测集分布：按 scene_group 汇总 case 数 / 帧数 / 帧占比
  const groups = useMemo(() => {
    const m = new Map<string, { group: string; cases: number; frames: number }>();
    for (const c of report.cases) {
      const g = c.sceneGroup || UNCATEGORIZED;
      const acc = m.get(g) ?? { group: g, cases: 0, frames: 0 };
      acc.cases += 1;
      acc.frames += c.frames;
      m.set(g, acc);
    }
    return [...m.values()].sort((a, b) => b.frames - a.frames);
  }, [report.cases]);

  const evalRegion = summary.evalRegion
    ? summary.evalRegion
        .split(',')
        .map((r) => EVAL_REGION_CN[r] ? `${EVAL_REGION_CN[r]}（${r}）` : r)
        .join('、')
    : EMPTY;

  // ── 概览 ──
  const kpis: { key: keyof OccHaloHeadline; label: string; sub?: string; tech?: string; primary?: boolean }[] = vis
    ? [
        {
          key: 'macroRayiou',
          label: 'Macro RayIoU',
          primary: true,
          tech: '11 个非 road 类射线计数累加后求 RayIoU，再对 ΣGT>0 的类平均（不含 driveable）',
        },
        { key: 'letrayF1lFront', label: 'LET-Ray F1L', sub: '前向扇区 · class_agnostic', primary: true },
        { key: 'letrayRayIouFront', label: 'LET-Ray RayIoU', sub: '前向扇区 · class_agnostic' },
        { key: 'letrayMf1l', label: 'LET-Ray mF1L', sub: '前向 · class_aware · gt>0 类平均' },
        { key: 'occupancyIou', label: '占据 IoU', sub: '非 0 即占据' },
      ]
    : [
        {
          key: 'miou',
          label: '语义 mIoU',
          primary: true,
          tech: '各类 ΣI/ΣU 后对 ΣU>0 的类求平均，含 driveable_surface',
        },
        { key: 'godF1', label: 'Instance GOD F1', sub: `${primaryIou} · class_aware`, primary: true },
        { key: 'godMf1', label: 'god_mF1', sub: `${primaryIou} · gt>0 类平均` },
        { key: 'statusMiou', label: 'status mIoU', sub: 'unknown / free / occupied / filter' },
        { key: 'occupancyIou', label: '占据 IoU', sub: '非 0 即占据' },
      ];

  const overviewTab = (
    <>
      <ReadingGuide headline={headline} cases={report.cases} vision={vis} help={help} />

      <Panel title="模型信息" code="model_metadata">
        <div className="pe-sides" style={{ gridTemplateColumns: compare ? '1fr 1fr' : '1fr' }}>
          {compare && <SideInfo header={compare.base} side="base" />}
          <SideInfo header={header} side="current" />
        </div>
        <div className="occ-meta">
          <span>评测口径：{vis ? '纯视觉 vision_only' : '前融合 fusion'}</span>
          <span>halo case：{fmtCount(header.haloCases)} / 回传 {fmtCount(header.withMetrics)}</span>
          <span>
            <HelpLabel label="匹配帧" help={help.frames} />：{fmtCount(summary.frames)}
          </span>
          <span>有效格子：{fmtCount(summary.validCellCount)}</span>
          <span>评测区域：{evalRegion}</span>
          {summary.timeAlignToleranceSec !== null && (
            <span>时间对齐容差：{summary.timeAlignToleranceSec} s</span>
          )}
          <span>标准：{summary.standard || EMPTY}</span>
          {summary.haloReference && (
            <Tooltip title={summary.haloReference}>
              <span className="occ-meta-ref">halo：{summary.haloReference}</span>
            </Tooltip>
          )}
        </div>
      </Panel>

      <Panel
        title="主要指标"
        desc={
          compare
            ? `Δ 为与基线 Job ${compare.base.jobId} 在两侧都有 halo 数据的 ${compare.comparedCases} 个 case 交集上的差值（pp）；数值本身为本次全部 halo case。`
            : '全部 halo case 的计数先求和、再算比率（不是各 case 比率的平均）。'
        }
      >
        <div className="pe-kpis">
          {kpis.map((k) => (
            <Kpi
              key={k.key}
              label={k.label}
              value={headline[k.key]}
              delta={delta ? delta[k.key] : undefined}
              sub={k.sub}
              help={withTech(help[k.key], k.tech)}
              primary={k.primary}
            />
          ))}
        </div>
      </Panel>

      {vis ? (
        <>
          <Panel
            title="语义总表"
            code="semantic_rayiou_summary"
            desc="11 个非 road 类按射线计数累加后求 RayIoU，再对真值有射线的类求平均（不含 driveable）。"
          >
            <div className="occ-kv">
              <span>Macro RayIoU <b>{fmtRatio(vision.macroRayiou)}</b></span>
              <span>total_frames <b>{fmtCount(summary.frames)}</b></span>
            </div>
          </Panel>
          <Panel title="GOD 主表（LET-Ray，前向扇区）" code="god_raycast_overview">
            <LetRayTable rows={vision.letrayOverview} kind="overview" />
          </Panel>
        </>
      ) : (
        <>
          <Panel
            title="语义总表"
            code="semantic_summary"
            desc="各类 ΣI/ΣU 后对 ΣU>0 的类求平均，含 driveable_surface。"
          >
            <div className="occ-kv">
              <span>avg mIoU <b>{fmtRatio(fusion.miou)}</b></span>
              <span>status mIoU <b>{fmtRatio(status.miou)}</b></span>
              <span>total_frames <b>{fmtCount(summary.frames)}</b></span>
            </div>
          </Panel>
          <Panel title={`GOD 概览（Instance-IoU ${primaryIou}）`} code="god_overview">
            <GodTable rows={god.overview} kind="overview" />
          </Panel>
        </>
      )}

      <Panel title="占据总表" code="occupancy_summary" desc="非 0 即占据（driveable 也算），格子计数累加后算比例。">
        <OccupancyTable occupancy={occupancy} />
      </Panel>

      {compare && <ComparePanel compare={compare} variant={variant} help={help} />}
    </>
  );

  // ── 评测集分布 ──
  const caseColumns: ColumnsType<OccBenchmarkCase> = [
    {
      title: helpLabel('caseId', 'Case'),
      dataIndex: 'caseId',
      key: 'caseId',
      width: 150,
      fixed: 'left',
      render: (v) => v || EMPTY,
    },
    {
      title: helpLabel('apiCode', 'API_CODE'),
      dataIndex: 'apiCode',
      key: 'apiCode',
      width: 120,
      render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} />,
    },
    {
      title: helpLabel('sceneGroup', '场景分组'),
      dataIndex: 'sceneGroup',
      key: 'sceneGroup',
      width: 120,
      render: (v: string | null) => sceneGroupDisplay(v),
    },
    countCol<OccBenchmarkCase>(helpLabel('frames', '匹配帧'), (r) => r.frames, 'frames', 96),
    ratioCol<OccBenchmarkCase>(
      helpLabel('share', '帧占比'),
      (r) => (totalFrames ? r.frames / totalFrames : null),
      'share',
      96,
    ),
    ...HEADLINE_LABELS.filter((h) => !h.variant || h.variant === variant).map((h) => ({
      ...ratioCol<OccBenchmarkCase>(helpLabel(h.key, h.label), (r) => r.metrics[h.key], h.key, 150),
      render: (_: unknown, r: OccBenchmarkCase) => {
        const cur = r.metrics[h.key];
        const base = r.baseMetrics?.[h.key];
        const d = cur !== null && cur !== undefined && base !== null && base !== undefined ? (cur - base) * 100 : null;
        return (
          <span>
            {fmtRatio(cur)}
            {d !== null && (
              <span style={{ marginLeft: 4, fontSize: 11, color: deltaColor(d) }}>{fmtPp(d, 1)}</span>
            )}
          </span>
        );
      },
    })),
    {
      title: '操作',
      key: 'action',
      width: 60,
      fixed: 'right',
      render: (_, r) => (
        <Tooltip title={r.taskId ? 'Task 日志' : '该场景无 task，无日志'}>
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            disabled={!r.taskId}
            onClick={() => r.taskId && onViewLog(r.taskId)}
          />
        </Tooltip>
      ),
    },
  ];

  const distributionTab = (
    <>
      <Panel title="评测集分布" desc="按场景分组统计 halo case 数与匹配帧数；帧占比 = 该组匹配帧 / 全部匹配帧。">
        <Table
          rowKey="group"
          size="small"
          dataSource={groups}
          pagination={false}
          tableLayout="fixed"
          columns={[
            { title: '场景分组', key: 'group', render: (_, g) => sceneGroupDisplay(g.group === UNCATEGORIZED ? null : g.group) },
            { title: 'case 数', key: 'cases', width: 100, align: 'right', render: (_, g) => fmtCount(g.cases) },
            {
              title: helpLabel('frames', '匹配帧'),
              key: 'frames',
              width: 120,
              align: 'right',
              render: (_, g) => fmtCount(g.frames),
            },
            {
              title: <HelpLabel label="帧占比" help="该分组匹配帧 ÷ 所有场景匹配帧之和，即这一组在汇总值里的权重。" />,
              key: 'share',
              width: 100,
              align: 'right',
              render: (_, g) => fmtRatio(totalFrames ? g.frames / totalFrames : null),
            },
          ]}
        />
      </Panel>
      <Panel
        title="逐 case 指标"
        desc={`每个 case 用自己的计数按同一套公式计算；${compare ? '数值右侧小字为相对基线同 case 的 Δ（pp）；' : ''}列随顶部口径切换。`}
      >
        <Table<OccBenchmarkCase>
          rowKey="sceneId"
          size="small"
          dataSource={report.cases}
          columns={caseColumns}
          tableLayout="fixed"
          scroll={{ x: 1400 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '500'],
            showTotal: (t) => `总共 ${t} 个 case`,
          }}
        />
      </Panel>
    </>
  );

  // ── Metrics 说明 ──
  const tolSec = summary.timeAlignToleranceSec ?? 0.05;
  const guideRows: MetricGuideRow[] = [
    {
      group: '基础概念',
      name: '匹配帧',
      plain: help.frames,
      formula: `|t_仿真 − t_真值| ≤ ${tolSec} s 才配对`,
      note: '每个场景的匹配帧数见「2 评测集分布」；为 0 的场景不参与汇总。',
    },
    {
      group: '基础概念',
      name: '帧占比',
      plain: help.share,
      formula: '场景匹配帧 ÷ Σ 所有场景匹配帧',
      note: '帧多的场景对汇总值影响大。',
    },
    {
      group: '基础概念',
      name: 'IoU（重合度）',
      plain: (
        <>
          两边都是这一类的格子 ÷ 任意一边是这一类的格子。例：真值 100 格、仿真 120 格、重合 80 格 → 80 ÷ (100 + 120 − 80) = 57%。
        </>
      ),
      formula: 'IoU = I / U = I / (G + P − I)',
      note: 'I 重合、U 合并、G 真值格子数、P 仿真格子数；精确率 = I / P（仿真涂的格子里对的比例），召回率 = I / G（真值格子被涂到的比例）。',
    },
    {
      group: '基础概念',
      name: '汇总口径',
      plain: '汇总值不是各场景数值的平均：先把所有场景的格子 / 射线 / 障碍物计数加起来，再算一次比例，所以帧多的场景权重大（就是「帧占比」）。',
      formula: '各 case 计数求和后再算比率，分母 +1e-6',
      note: '不使用 case 上报的 summary 比率；分母为 0 时比率为 0。',
    },
    {
      group: '有没有东西 / 能不能走',
      name: '占据 IoU',
      plain: help.occupancyIou,
      formula: '非 0 即占据，ΣI / ΣU',
      note: 'driveable（路面）也计为占据。',
    },
    {
      group: '有没有东西 / 能不能走',
      name: 'status mIoU',
      plain: help.statusMiou,
      formula: 'unknown / free / occupied / filter 四类的 IoU 平均',
      note: 'status 由语义推出：0 unknown（空白）、1 free（能走）、{2,3,7..12} occupied（固定物体）、{4,5,6} filter（车·人等会动的东西）。',
    },
    {
      group: '是什么东西',
      name: '语义 mIoU（前融合）',
      plain: help.miou,
      formula: 'IoU_c = ΣI / ΣU，对 ΣU>0 的类平均（含 driveable）',
      note: '逐格比较；仿真把真值为空的格子涂成某一类也算错（涂得太满）。',
    },
    {
      group: '是什么东西',
      name: 'Macro RayIoU（纯视觉）',
      plain: help.macroRayiou,
      formula: '各类 ΣMatched / (ΣGT + ΣPred − ΣMatched)，对 ΣGT>0 的类平均',
      note: '不含 driveable_surface；sector = all、class_aware。',
    },
    {
      group: '障碍物检测准不准',
      name: 'Instance GOD F1 / god_mF1（前融合）',
      plain: (
        <>
          {help.godF1}
          <br />
          {help.godMf1}
        </>
      ),
      formula: '连通块匈牙利匹配，IoU ≥ 0.2 计命中；recall = ΣTP/ΣGT，precision = ΣTP/ΣPred，F1 = 2·R·P / (R+P)',
      note: 'god_mF1@0.20 = gt>0 的类 F1 平均；curb（路沿）按 0.3 m 距离容差单独统计。',
    },
    {
      group: '障碍物检测准不准',
      name: 'LET-Ray 射线（纯视觉）',
      plain: '从车向四周发 720 条射线，看每条射线上第一个碰到的障碍物：位置在距离容忍内才算对上（分类别口径还要求类别一致）。主指标只看车前方扇区。',
      formula: 'LET-Ray IoU（RayIoU）= Matched / (GT + Pred − Matched)',
      note: '射线首阻挡面的类别一致且深度在容差内才算命中。',
    },
    {
      group: '障碍物检测准不准',
      name: '距离容忍',
      plain: '允许的距离误差：近处至少 0.5 m，远处放宽到真实距离的 10%。例：真值在 20 m 处，仿真报在 18~22 m 之间都算对上。',
      formula: 'T = max(0.5 m, 10% × GT 距离)',
      note: '预测偏近或偏远都可匹配。',
    },
    {
      group: '障碍物检测准不准',
      name: 'LET-Ray F1L / mF1L',
      plain: (
        <>
          {help.letrayF1lFront}
          <br />
          {help.letrayMf1l}
        </>
      ),
      formula: 'precision_L = Σaffinity / ΣPred；F1L = 2·R·PL / (R+PL)',
      note: '在容差内按深度误差连续降权；mF1L = gt>0 的类 F1L 平均。',
    },
    {
      group: '障碍物检测准不准',
      name: '多报 / 漏报（False / Missed Block）',
      plain: '多报：真值这个方向是空的，仿真却说有东西挡着；漏报：真值有东西挡着，仿真却说是空的。这两个是错误率，越低越好。',
      formula: 'ΣFB / max(Σvalid − ΣGT, 0)；ΣMB / ΣGT',
      note: 'GT free 但 Pred 阻挡（多报）/ GT 阻挡但 Pred free（漏报）。',
    },
  ];
  const groupSpan = (i: number): number => {
    if (i > 0 && guideRows[i - 1].group === guideRows[i].group) {
      return 0;
    }
    let n = 1;
    while (i + n < guideRows.length && guideRows[i + n].group === guideRows[i].group) {
      n += 1;
    }
    return n;
  };
  const guideTab = (
    <>
      <Panel title="先打个比方">
        <div className="occ-guide-intro">
          把车周围的地面切成 <b>10 cm 一格的棋盘</b>：<b>真值</b>是标准答案（每个格子涂一种类别颜色，比如路面、车、行人），
          <b>仿真输出的 OCC</b> 是答卷。下面的指标都是从不同角度给「答卷和标准答案像不像」打分，范围 0~100%；
          除了注明的错误率（多报 / 漏报、距离误差）以外，<b>都是越高越好</b>。
          建议由粗到细地看：有没有东西（占据 IoU）→ 能不能走（status mIoU）→ 是什么东西（语义）→ 障碍物检测准不准（GOD）。
        </div>
      </Panel>
      <Panel title="指标说明" code="god_metric_guide / god_rayiou_guide" desc="白话解释在前；「公式 / 口径」列保留与 halo 报表一致的严格定义。">
        <Table<MetricGuideRow>
          rowKey="name"
          size="small"
          dataSource={guideRows}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 1080 }}
          className="occ-guide-table"
          columns={[
            {
              title: '看什么',
              key: 'g',
              width: 110,
              onCell: (_, i) => ({ rowSpan: groupSpan(i ?? 0) }),
              render: (_, r) => <span className="occ-guide-group">{r.group}</span>,
            },
            { title: '指标', key: 'm', width: 170, render: (_, r) => <b>{r.name}</b> },
            { title: '白话解释', key: 'p', render: (_, r) => r.plain },
            { title: '公式 / 口径', key: 'f', width: 240, render: (_, r) => <span className="occ-guide-formula">{r.formula}</span> },
            { title: '补充', key: 'n', width: 220, render: (_, r) => <span className="occ-guide-note">{r.note}</span> },
          ]}
        />
      </Panel>
      <Panel title="热力图怎么读" desc="「4 语义指标」「5 占据指标」「6 GOD 指标」页签各有一张对应指标的热力图。">
        <div className="occ-guide-intro">
          热力图是<b>从车顶往下看的俯视图</b>：车头朝上，<b>图的左边就是车的左边</b>，橙色小车 = 本车位置。
          行是前后距离（上方 = 车前更远），列是左右距离（单位 m，左正右负）。仿真 OCC 只覆盖{COVERAGE_TEXT}。
        </div>
        <HeatmapReadingNote />
      </Panel>
    </>
  );

  // ── 语义指标 ──
  const semanticTab = vis ? (
    <>
      <Panel title="分类别 RayIoU" code="semantic_rayiou_class" desc="class_aware、sector=all；非 road 类，ΣGT=0 的类不计入 Macro RayIoU。">
        <RayIouClassTable rows={vision.rayiouClasses} macro={vision.macroRayiou} />
      </Panel>
      <Panel title="语义 RayIoU 空间热力图（GT 首阻挡位置，5 m 网格）" desc="每箱对 ΣGT>0 的类求 RayIoU 平均。">
        <OccHeatmap xBins={spatial.xBins} yBins={spatial.yBins} cells={heat((c) => c.rayiou)} metric="语义 RayIoU" />
      </Panel>
    </>
  ) : (
    <>
      <Panel title="分类别 IoU" code="class_breakdown" desc="ΣU=0 的类显示 0%（halo 口径）且不计入 mIoU。">
        <SemanticIouTable rows={fusion.semanticClasses} miou={fusion.miou} />
      </Panel>
      <Panel
        title="status 指标"
        desc="把 12 种类别合成 4 种状态再比较：空白（unknown，类别 0）/ 能走（free，类别 1 路面）/ 被固定物体挡住（occupied，类别 2、3、7~12）/ 被车·人等会动的东西挡住（filter，类别 4、5、6）。"
      >
        <StatusTable rows={status.classes} miou={status.miou} />
      </Panel>
      <Panel title="语义 mIoU 空间热力图（5 m 网格）" desc="每箱对 U>0 的类求 IoU 平均；空格表示该箱内无 U>0 的类。">
        <OccHeatmap xBins={spatial.xBins} yBins={spatial.yBins} cells={heat((c) => c.semanticMiou)} metric="语义 mIoU" />
      </Panel>
    </>
  );

  // ── 占据指标 ──
  const occupancyTab = (
    <>
      <Panel title="占据总表" code="occupancy_summary">
        <OccupancyTable occupancy={occupancy} />
      </Panel>
      <Panel title="占据 IoU 空间热力图（5 m 网格）">
        <OccHeatmap xBins={spatial.xBins} yBins={spatial.yBins} cells={heat((c) => c.occupancyIou)} metric="占据 IoU" />
      </Panel>
    </>
  );

  // ── GOD 指标 ──
  const godTab = vis ? (
    <>
      <Panel title="LET-Ray 主表（前向扇区）" code="god_raycast_overview">
        <LetRayTable rows={vision.letrayOverview} kind="overview" />
      </Panel>
      <Panel
        title="LET-Ray 分类别（前向扇区，class_aware）"
        code="god_raycast_class"
        desc="mF1L = 真值有射线的类的 F1L 平均。"
      >
        <LetRayTable rows={vision.letrayClasses} kind="class" mf1l={vision.mf1l} />
      </Panel>
      <Panel title="扇区" code="god_raycast_sector" desc="class_aware、class_name = all。">
        <LetRayTable rows={vision.sectors} kind="sector" />
      </Panel>
      <Panel title="距离误差分布（前向扇区，命中射线）" desc="命中射线的 |预测距离 − 真值距离| 分档。">
        <ErrorHistogram rows={vision.letrayOverview} buckets={vision.errorBuckets} />
      </Panel>
    </>
  ) : (
    <>
      <Panel title={`Instance-IoU 概览 ${primaryIou}`} code="god_overview">
        <GodTable rows={god.overview} kind="overview" />
      </Panel>
      <Panel title="阈值" code="god_thresholds">
        <GodTable rows={god.thresholds} kind="threshold" />
      </Panel>
      <Panel
        title={`Instance-IoU 分类别 ${primaryIou}`}
        code="god_class_aware_breakdown"
        desc="curb 按距离容差 0.3 m 单独统计（recall 取 god_curb_recall、precision 取 god_curb_precision）；god_mF1 = gt>0 的类 F1 平均。"
      >
        <GodTable rows={god.classes} kind="class" mf1={god.mf1} primaryIou={primaryIou} />
      </Panel>
      <Panel
        title="Instance GOD F1 空间热力图（5 m 网格）"
        extra={
          <Segmented
            size="small"
            value={godHeatMode}
            onChange={(v) => setGodHeatMode(v as 'aware' | 'agnostic')}
            options={[
              { value: 'aware', label: 'class_aware' },
              { value: 'agnostic', label: 'class_agnostic' },
            ]}
          />
        }
      >
        <OccHeatmap
          xBins={spatial.xBins}
          yBins={spatial.yBins}
          cells={heat((c) => (godHeatMode === 'aware' ? c.godF1Aware : c.godF1Agnostic))}
          metric={`Instance GOD F1（${godHeatMode === 'aware' ? '分类别' : '不分类别'}）`}
        />
      </Panel>
    </>
  );

  return (
    <div className="occ-bench">
      <div className="occ-bench-bar">
        <span className="occ-bench-title">OCC Benchmark</span>
        <Tag bordered={false} color="geekblue" style={{ marginInlineEnd: 0 }}>
          halo occ_benchmark 口径
        </Tag>
        <Segmented
          value={variant}
          onChange={(v) => setVariant(v as OccVariant)}
          options={VARIANT_OPTIONS}
        />
        <span className="occ-bench-sub">
          评测区域：{evalRegion} · {fmtCount(header.haloCases)} 个 case · {fmtCount(summary.frames)} 帧
        </span>
      </div>
      <Tabs
        className="occ-bench-tabs"
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'overview', label: '1 概览', children: overviewTab },
          { key: 'distribution', label: '2 评测集分布', children: distributionTab },
          { key: 'guide', label: '3 Metrics 说明', children: guideTab },
          { key: 'semantic', label: '4 语义指标', children: semanticTab },
          { key: 'occupancy', label: '5 占据指标', children: occupancyTab },
          { key: 'god', label: '6 GOD 指标', children: godTab },
        ].map((it) => ({ ...it, children: <div className="occ-bench-pane">{it.children}</div> }))}
      />
    </div>
  );
}

function OccupancyTable({ occupancy }: { occupancy: OccBenchmarkReportData['occupancy'] }) {
  return (
    <Table
      rowKey={() => 'occ'}
      size="small"
      dataSource={[occupancy]}
      pagination={false}
      tableLayout="fixed"
      columns={[
        { title: 'occupancy_iou', key: 'iou', align: 'right', render: (_, o) => fmtRatio(o.iou) },
        { title: 'precision', key: 'p', align: 'right', render: (_, o) => fmtRatio(o.precision) },
        { title: 'recall', key: 'r', align: 'right', render: (_, o) => fmtRatio(o.recall) },
        { title: 'f1', key: 'f1', align: 'right', render: (_, o) => fmtRatio(o.f1) },
        { title: 'gt_count', key: 'gt', align: 'right', render: (_, o) => fmtCount(o.gt) },
        { title: 'pred_count', key: 'pred', align: 'right', render: (_, o) => fmtCount(o.pred) },
      ]}
    />
  );
}

function ComparePanel({
  compare,
  variant,
  help,
}: {
  compare: NonNullable<OccBenchmarkReportData['compare']>;
  variant: OccVariant;
  help: Record<OccHelpKey, React.ReactNode>;
}) {
  const rows = HEADLINE_LABELS.filter((h) => !h.variant || h.variant === variant);
  return (
    <Panel
      title="与基线对比"
      desc={`只在两侧都有 halo 数据的 case 交集上计算（${compare.comparedCases} 个 case；基线 halo case ${compare.base.haloCases}）。`}
    >
      {compare.comparedCases === 0 ? (
        <div className="pe-report-empty">两侧没有共同的 halo case，无法对比</div>
      ) : (
        <Table
          rowKey="key"
          size="small"
          dataSource={rows}
          pagination={false}
          tableLayout="fixed"
          columns={[
            { title: '指标', key: 'label', render: (_, h) => <HelpLabel label={h.label} help={help[h.key]} /> },
            {
              title: `基线 Job ${compare.base.jobId}`,
              key: 'base',
              align: 'right',
              render: (_, h) => fmtRatio(compare.baseHeadline?.[h.key]),
            },
            {
              title: '本次',
              key: 'cur',
              align: 'right',
              render: (_, h) => fmtRatio(compare.currentHeadline?.[h.key]),
            },
            {
              title: 'Δ',
              key: 'delta',
              align: 'right',
              render: (_, h) => {
                const d = compare.delta?.[h.key];
                return <span style={{ color: deltaColor(d) }}>{fmtPp(d)}</span>;
              },
            },
          ]}
        />
      )}
    </Panel>
  );
}
